'use client';
import './SquareCard.style.scss';
import cn from 'classnames';
import { CMSPlainImageData } from '../component-registry';
import { useUser } from '@/components/hook/user';
import Text from '@/components/common/Text/Text';
import CMSResponsiveImage from '@/components/common/CMSResponsiveImage/CMSResponsiveImage';
import { useRouter } from 'next/navigation';
import Button from '@/components/common/Button/Button';
import { CSSProperties, useEffect, useState } from 'react';

export type SquareCardProps = {
  loggedOut: {
    title: string;
    description: string;
    image: CMSPlainImageData;
    cta: {
      text: string;
      href: string;
    };
  };
  loggedIn?: {
    title?: string;
    description?: string;
    image?: CMSPlainImageData;
    cta?: {
      text: string;
      href: string;
    };
  };
  slightlySmallerCopy?: boolean;
  customStyling?: CSSProperties;
};

function SquareCard(props: SquareCardProps) {
  const {
    loggedOut: loggedOutProps,
    loggedIn: loggedInProps,
    slightlySmallerCopy,
    customStyling
  } = props;
  const { value: user } = useUser();
  const router = useRouter();

  const propsToUse = {
    title:
      user && loggedInProps?.title
        ? loggedInProps.title
        : loggedOutProps?.title ?? '',
    description:
      user && loggedInProps?.description
        ? loggedInProps.description
        : loggedOutProps?.description ?? '',
    image: {
      image:
        user && loggedInProps?.image?.image
          ? loggedInProps.image.image
          : loggedOutProps?.image?.image ?? '',
      imageAltText:
        user && loggedInProps?.image?.imageAltText
          ? loggedInProps.image.imageAltText
          : loggedOutProps?.image?.imageAltText ?? '',
      imageHeight:
        user && loggedInProps?.image?.imageHeight
          ? loggedInProps.image.imageHeight
          : loggedOutProps?.image?.imageHeight ?? 0,
      imageWidth:
        user && loggedInProps?.image?.imageWidth
          ? loggedInProps.image.imageWidth
          : loggedOutProps?.image?.imageWidth ?? 0
    },
    cta: {
      text:
        user && loggedInProps?.cta?.text
          ? loggedInProps.cta.text
          : loggedOutProps?.cta?.text ?? '',
      href:
        user && loggedInProps?.cta?.href
          ? loggedInProps.cta.href
          : loggedOutProps?.cta?.href ?? ''
    }
  };

  // These error checks make it easier for the Builder user to see if some required input is missing
  if (!propsToUse.title) {
    return <SquareCardError message='No title provided' />;
  }

  if (!propsToUse.description) {
    return <SquareCardError message='No description provided' />;
  }

  if (!propsToUse.image.image) {
    return <SquareCardError message='No image provided' />;
  }

  if (!propsToUse.image.imageAltText) {
    return <SquareCardError message='No image alt text provided' />;
  }

  if (!propsToUse.image.imageHeight) {
    return <SquareCardError message='No image height provided' />;
  }

  if (!propsToUse.image.imageWidth) {
    return <SquareCardError message='No image width provided' />;
  }

  if (!propsToUse.cta.href) {
    return <SquareCardError message='No CTA href provided' />;
  }

  if (!propsToUse.cta.text) {
    return <SquareCardError message='No CTA text provided' />;
  }

  return (
    <div
      className={cn('SquareCard')}
      style={{ ...customStyling }}
    >
      {propsToUse.image && propsToUse.image.image ? (
        <CMSResponsiveImage
          className={cn('SquareCard-image-container')}
          desktopImgClassName={cn('SquareCard-image-desktop')}
          mobileImgClassName={cn('SquareCard-image-mobile')}
          data={{
            desktop: propsToUse.image,
            mobile: propsToUse.image
          }}
        />
      ) : null}
      <div
        className={cn(
          'SquareCard-copy',
          slightlySmallerCopy && 'slightlySmallerCopy'
        )}
      >
        <Text
          className={cn('SquareCard-title')}
          variant='title-m'
        >
          {propsToUse.title}
        </Text>
        <Text
          className={cn(
            'SquareCard-description',
            slightlySmallerCopy && 'slightlySmallerCopy'
          )}
          variant='text-m'
        >
          {propsToUse.description.replaceAll('\\n', '')}

          {/* This allows the Builder user to add blank lines below the description
              to match the height of the descriptions of horizontal neighbors so that, 
              if desired, neighboring CTAs line up vertically */}
          {['\n', ...(propsToUse.description?.match(/\\n/g) || [])].map(
            (_, i) => (
              <br
                key={i}
                className={cn('SquareCard-description-added-linebreak')}
              />
            )
          )}
        </Text>
        <Button
          className={cn(
            'SquareCard-cta',
            slightlySmallerCopy && 'slightlySmallerCopy'
          )}
          variant='underline'
          onClick={() => router.push(propsToUse.cta.href)}
        >
          {/* This allows the Builder user to manually add linebreaks to the CTA text.
            Without this, the auto text-wrapping that occurs would cause the bottom
            border (which we use as an underline) of the CTA container would extend
            past the width of the text, which would look bad. */}
          {propsToUse.cta.text.includes('\\n') ? (
            <>
              <span>{propsToUse.cta.text.split('\\n')[0]}</span>
              <br />
              <span>{propsToUse.cta.text.split('\\n')[1]}</span>
            </>
          ) : (
            <span>{propsToUse.cta.text}</span>
          )}
        </Button>
      </div>
    </div>
  );
}

export default SquareCard;

// This component makes it easy to show the Builder user a fully-rendered error message quickly explaining which required input is missing
function SquareCardError({ message }: { message: string }) {
  return (
    <div className={cn('SquareCardError')}>
      <Text
        className={cn('SquareCardError-message')}
        variant='title-l'
      >
        {message}
      </Text>
    </div>
  );
}
